import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Loader2, RotateCw } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lightweight markdown to HTML converter for itinerary display
function md(text = "") {
  let t = text.trim();
  t = t.replace(/^### (.+)$/gm, "<h3>$1</h3>");
  t = t.replace(/^## (.+)$/gm, "<h2>$1</h2>");
  t = t.replace(/^# (.+)$/gm, "<h2>$1</h2>");
  t = t.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  t = t.replace(/\*(.+?)\*/g, "<em>$1</em>");
  // bullets
  t = t.replace(/^(?:- |\* )(.+)$/gm, "<li>$1</li>");
  t = t.replace(/(<li>[\s\S]+?<\/li>)/g, (m) => `<ul>${m}</ul>`);
  // paragraphs
  t = t
    .split(/\n{2,}/)
    .map((block) => (/^<(h\d|ul|li)/.test(block.trim()) ? block : `<p>${block.replace(/\n/g, " ")}</p>`))
    .join("");
  return t;
}

export default function AITripPlanner({ open, onClose }) {
  const [form, setForm] = useState({
    destination: "",
    duration: "7 days",
    travelers: "2 adults",
    style: "Luxury escape",
    budget: "Premium",
    interests: "",
  });
  const [itinerary, setItinerary] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const generate = async (e) => {
    e?.preventDefault();
    setErr("");
    setLoading(true);
    setItinerary("");
    try {
      const { data } = await axios.post(`${API}/trip-planner`, form);
      setItinerary(data.itinerary || "");
    } catch (e2) {
      setErr("Our concierge AI is taking a break. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setItinerary("");
    setErr("");
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#0A2342]/70 backdrop-blur-md"
          onClick={onClose}
          data-testid="ai-trip-planner-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col"
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A227] to-[#D4B34A] flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </span>
                <div>
                  <div className="font-display text-lg text-[#0A2342] leading-none">AI Trip Planner</div>
                  <div className="text-xs text-[#6B7280] mt-1">Powered by Travwave Concierge AI</div>
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="ai-planner-close"
                className="w-10 h-10 rounded-full bg-[#F8F9FB] hover:bg-gray-200 flex items-center justify-center"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-[#0A2342]" />
              </button>
            </div>

            <div className="overflow-y-auto p-6 md:p-8">
              {!itinerary && !loading && (
                <form onSubmit={generate} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="ai-planner-form">
                  <Field label="Destination (or leave blank)" value={form.destination} onChange={set("destination")} placeholder="Maldives, Japan…" testid="ai-destination" />
                  <Field label="Duration" value={form.duration} onChange={set("duration")} testid="ai-duration" />
                  <Field label="Travellers" value={form.travelers} onChange={set("travelers")} testid="ai-travelers" />
                  <Select label="Travel style" value={form.style} onChange={set("style")} testid="ai-style">
                    {["Luxury escape", "Honeymoon", "Family", "Adventure", "Wellness", "Cultural"].map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </Select>
                  <Select label="Budget" value={form.budget} onChange={set("budget")} testid="ai-budget">
                    {["Comfortable", "Premium", "Ultra Luxury"].map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </Select>
                  <Field label="Interests" value={form.interests} onChange={set("interests")} placeholder="Fine dining, wellness…" testid="ai-interests" />
                  <button data-testid="ai-generate-btn" type="submit" className="btn-gold md:col-span-2 justify-center">
                    <Sparkles className="w-4 h-4" /> Generate Itinerary
                  </button>
                </form>
              )}

              {loading && (
                <div className="py-16 text-center" data-testid="ai-loading">
                  <Loader2 className="w-10 h-10 animate-spin text-[#C9A227] mx-auto" />
                  <p className="mt-6 text-[#0A2342] font-display text-xl">Crafting your bespoke journey…</p>
                  <p className="text-sm text-[#6B7280] mt-2">Our AI concierge is consulting maps, menus and 5-star hotels.</p>
                </div>
              )}

              {err && <div className="mt-4 text-sm text-red-500" data-testid="ai-error">{err}</div>}

              {itinerary && !loading && (
                <div data-testid="ai-itinerary">
                  <div className="overline">Your bespoke itinerary</div>
                  <div className="mt-3 itinerary-content" dangerouslySetInnerHTML={{ __html: md(itinerary) }} />
                  <div className="mt-8 flex flex-col sm:flex-row gap-3">
                    <button data-testid="ai-regen-btn" onClick={reset} className="btn-navy">
                      <RotateCw className="w-4 h-4" /> Plan another
                    </button>
                    <a
                      href={`https://wa.me/919876543210?text=${encodeURIComponent("Hi Travwave, I'd love to book this itinerary I designed.")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="ai-book-btn"
                      className="btn-gold"
                    >
                      Make it real <Sparkles className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[#6B7280] mb-2">{label}</label>
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-[#F8F9FB] border border-gray-200 rounded-full px-5 py-3"
      />
    </div>
  );
}

function Select({ label, value, onChange, children, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[#6B7280] mb-2">{label}</label>
      <select value={value} onChange={onChange} data-testid={testid} className="w-full bg-[#F8F9FB] border border-gray-200 rounded-full px-5 py-3">
        {children}
      </select>
    </div>
  );
}
